import { Head, router } from "@inertiajs/react"
import { FileText, ChevronRight } from "lucide-react"
import { AdminLayout } from "../components/AdminLayout"
import { Card, CardContent } from "@/components/ui/card"
import { statusConfig, StatusBadge } from "./StatusBadge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

export default function CollectiveApplicationsIndex({
  currentUser,
  applications,
  pendingArticleCount,
  applicationsPendingCount,
  currentStatus,
  pagination,
}) {
  const handleStatusFilter = (value) => {
    const params = value === "all" ? {} : { status: value }
    router.get("/app/admin/collective_applications", params, { preserveState: true })
  }

  return (
    <AdminLayout currentUser={currentUser} pendingCount={pendingArticleCount} applicationsPendingCount={applicationsPendingCount}>
      <Head title="Applications — Admin" />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Applications</h1>
            <p className="text-sm text-zinc-500 mt-1">
              {pagination?.totalCount ?? 0} application{pagination?.totalCount !== 1 ? "s" : ""}
              {currentStatus ? ` (${statusConfig[currentStatus]?.label || currentStatus})` : ""}
            </p>
          </div>
          <Select
            value={currentStatus || "all"}
            onValueChange={handleStatusFilter}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(statusConfig).map(([value, config]) => (
                <SelectItem key={value} value={value}>
                  {config.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {applications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-zinc-400">
                <FileText size={36} className="mb-3 opacity-40" />
                <p className="text-sm font-medium">No applications yet</p>
                <p className="text-xs mt-1">Applications will appear here</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden md:table-cell">Company</TableHead>
                    <TableHead className="hidden lg:table-cell">Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden sm:table-cell w-[120px]">Date</TableHead>
                    <TableHead className="w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications.map((app) => (
                    <TableRow
                      key={app.id}
                      className="cursor-pointer hover:bg-zinc-50"
                      onClick={() =>
                        router.visit(`/app/admin/collective_applications/${app.id}`)
                      }
                    >
                      <TableCell>
                        <p className="text-sm font-medium text-zinc-800">{app.fullName}</p>
                        <p className="text-xs text-zinc-400">{app.email}</p>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <p className="text-sm text-zinc-700">{app.companyName}</p>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <p className="text-sm text-zinc-500">{app.location}</p>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={app.status} />
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-xs text-zinc-400 whitespace-nowrap">
                        {new Date(app.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </TableCell>
                      <TableCell>
                        <ChevronRight size={14} className="text-zinc-300" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              {pagination.currentPage > 1 && (
                <PaginationItem>
                  <PaginationPrevious
                    href={`/app/admin/collective_applications?page=${pagination.currentPage - 1}${currentStatus ? `&status=${currentStatus}` : ""}`}
                  />
                </PaginationItem>
              )}
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    href={`/app/admin/collective_applications?page=${page}${currentStatus ? `&status=${currentStatus}` : ""}`}
                    isActive={page === pagination.currentPage}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
              {pagination.currentPage < pagination.totalPages && (
                <PaginationItem>
                  <PaginationNext
                    href={`/app/admin/collective_applications?page=${pagination.currentPage + 1}${currentStatus ? `&status=${currentStatus}` : ""}`}
                  />
                </PaginationItem>
              )}
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </AdminLayout>
  )
}
